import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Checkbox,
} from "@mui/material";
import { TableVirtuoso } from "react-virtuoso";
import useHttpRequests from "../../../hooks/useHttpRequests";

const thBackgoundColor = "#3a8ddd";
const thFontColor = "white";
const thFontWeight = "bold";
const paperStyles = { width: "100%", height: "calc(100vh - 550px)" };
const tableHeight = { height: "calc(100vh - 550px)" };

const columns = [
  {
    label: "",
    keyField: "fileTypeKey",
    dataField: "isChecked",
    width: 30,
    fieldType: "checkbox",
  },
  {
    label: "File Type Name",
    keyField: "fileTypeKey",
    dataField: "name",
    width: 200,
    fieldType: "string",
  },
  {
    label: "Description",
    keyField: "fileTypeKey",
    dataField: "description",
    width: 200,
    fieldType: "string",
  },
  {
    label: "Row to start with",
    keyField: "fileTypeKey",
    dataField: "rowToStartWith",
    fieldType: "number",
    numeric: true,
    width: 90,
  },
];

const VirtuosoTableComponents = {
  Scroller: React.forwardRef((props, ref) => (
    <TableContainer component={Paper} {...props} ref={ref} />
  )),
  Table: (props) => (
    <Table
      {...props}
      sx={{ borderCollapse: "separate", tableLayout: "fixed" }}
    />
  ),
  TableHead,
  TableRow: ({ item: _item, ...props }) => <TableRow {...props} />,
  TableBody: React.forwardRef((props, ref) => (
    <TableBody {...props} ref={ref} />
  )),
};

export const DataSourceFileTypes = (props) => {
  const { setSelectedRows, rowExists } = props;
  const [localData, setLocalData] = useState([]);
  const { sendRequest: fetchFileTypes } = useHttpRequests();

  const fixedHeaderContent = () => {
    return (
      <TableRow>
        {columns.map((column) => (
          <TableCell
            key={column.dataField}
            variant="head"
            align={column["fieldType"] === "boolean" ? "center" : "left"}
            style={{
              backgroundColor: thBackgoundColor,
              color: thFontColor,
              fontWeight: thFontWeight,
              padding: "8px 0px 8px 8px",
              width: column.width,
            }}
            sx={{
              backgroundColor: "background.paper",
            }}
          >
            {column.label}
          </TableCell>
        ))}
      </TableRow>
    );
  };

  const rowContent = (_index, row) => {
    return (
      <React.Fragment>
        {columns.map((col, colIdx) => (
          <TableCell
            sx={{ padding: "0px 0px 0px 8px" }}
            key={"cols_" + colIdx + "_" + row[col["keyField"]]}
            align={col["fieldType"] === "boolean" ? "center" : "left"}
          >
            {col["fieldType"] === "checkbox" && col["label"] === "" && (
              <Checkbox
                sx={{ p: 0 }}
                checked={row.isChecked}
                onChange={() => handleCheckboxChange(row.fileTypeKey)}
              />
            )}
            {row[col["keyField"]] !== row[col["dataField"]] &&
              col["fieldType"] !== "number" &&
              col["dataType"] !== "date" && <>{row[col["dataField"]]}</>}
            {col["fieldType"] === "number" && (
              <TextField
                type={col["fieldType"]}
                size="small"
                id={
                  col["fieldType"] + "_" + colIdx + "_" + row[col["keyField"]]
                }
                name={col["dataField"]}
                onChange={(event) =>
                  handleInputChange(event, row[col["keyField"]])
                }
                value={row[col["dataField"]]}
                maxLength={2}
              />
            )}
          </TableCell>
        ))}
      </React.Fragment>
    );
  };

  useEffect(() => {
    const getFileTypesResponse = (obj) => {
      if (!obj || !obj.isSuccess || obj.errorResponse) {
        console.log("File types fetch response returns error");
        return;
      }

      const fileTypesArray = obj["fileTypes"]?.map((t) => {
        return {
          fileTypeKey: t["fileTypeKey"],
          name: t["name"],
          description: t["description"],
          rowToStartWith: "",
          active: true,
          isChecked: false,
        };
      });

      let updateFileTypesArray = rowExists?.map((r) => {
        const tempIndex = fileTypesArray.findIndex(
          (x) => x.fileTypeKey === r.fileTypeKey
        );
        fileTypesArray[tempIndex]["rowToStartWith"] = r.rowToStartWith;
        fileTypesArray[tempIndex]["active"] = r.active;
        fileTypesArray[tempIndex]["isChecked"] = true;
      });

      const mySelectedRows = fileTypesArray.filter((row) => row.isChecked);
      const myUnSelectedRows = fileTypesArray.filter((row) => !row.isChecked);

      updateFileTypesArray = [...mySelectedRows, ...myUnSelectedRows];

      setLocalData(updateFileTypesArray);
      setSelectedRows(mySelectedRows);
    };

    fetchFileTypes(
      { url: "FileTypes/Active", method: "GET", header: true },
      getFileTypesResponse
    );
  }, []);

  const handleCheckboxChange = (fileTypeKey) => {
    const updatedData = localData.map((row) => {
      if (row.fileTypeKey === fileTypeKey) {
        return { ...row, isChecked: !row.isChecked };
      }
      return row;
    });

    const mySelectedRows = updatedData
      .filter((row) => row.isChecked)
      .sort((a, b) => a.name.localeCompare(b.name));
    const myUnSelectedRows = updatedData
      .filter((row) => !row.isChecked)
      .sort((a, b) => a.name.localeCompare(b.name));

    const updateFileTypesArray = [...mySelectedRows, ...myUnSelectedRows];

    setLocalData(updateFileTypesArray);
    setSelectedRows(mySelectedRows);
  };

  const handleInputChange = (event, id) => {
    const { name, value } = event.target;
    setLocalData((prevData) =>
      prevData.map((row) =>
        row[columns[0]["keyField"]] === id ? { ...row, [name]: value } : row
      )
    );
    setSelectedRows((prevData) =>
      prevData.map((row) =>
        row[columns[0]["keyField"]] === id ? { ...row, [name]: value } : row
      )
    );
  };

  return (
    <Paper sx={paperStyles}>
      <TableVirtuoso
        data={localData}
        components={VirtuosoTableComponents}
        fixedHeaderContent={fixedHeaderContent}
        itemContent={rowContent}
      />
    </Paper>
  );
};
